import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';

import { useCalendarSheet } from '../components/CalendarSheetProvider';
import { CalendarSheetProps } from '../components/types';
import {
    TransactionDateLabel,
    TransactionDateOptions,
    TransactionDateOptionsType,
    TransactionFilterOption,
    TransactionTypeLabel,
    TransactionTypesType,
} from '../hooks/types';
import { TransactionFilterCategory, UseTransactionFilter } from '../hooks/use-filter-transaction';

type FilterLabelKey = TransactionTypeLabel | TransactionDateLabel;
type SocialWalletFilterLabelKey = FilterLabelKey | 'purchases' | 'redemptions' | 'wallet:social_bonus';

/**
 * Social wallet products relabel a few filter options: purchases/redemptions for deposits/withdrawals,
 * and "Bonus" (wallet:social_bonus) for the Betr Bucks brand term. Returns an i18n key.
 */
export const getSocialWalletFilterLabelKey = (
    label: FilterLabelKey,
    hasSocialWallet: boolean
): SocialWalletFilterLabelKey => {
    if (!hasSocialWallet) {
        return label;
    }
    switch (label) {
        case 'deposits':
            return 'purchases';
        case 'withdrawals':
            return 'redemptions';
        case 'betr_bucks':
            return 'wallet:social_bonus';
        default:
            return label;
    }
};

export const getSelectedOptionLabel = <T extends string>(
    options: Array<TransactionFilterOption<T>>,
    category: TransactionFilterCategory,
    fallbackKey: string,
    filterOptions: UseTransactionFilter['filterOptions'],
    hasSocialWallet: boolean = false
): string => {
    const selectedOption = options.find(option => option.id === filterOptions[category]);
    if (!selectedOption) {
        return fallbackKey;
    }

    return getSocialWalletFilterLabelKey(selectedOption.label, hasSocialWallet);
};

export const getSelection = <T>(
    options: Array<TransactionFilterOption<T>>,
    defaultOption: TransactionFilterOption<T> & CalendarSheetProps,
    currentValue?: UseTransactionFilter['filterOptions']
): TransactionFilterOption<T> => {
    if (currentValue) {
        const selectedOption = options.find(option => option.id === currentValue);
        return selectedOption ? { id: selectedOption.id, label: selectedOption.label } : defaultOption;
    }
    return defaultOption;
};

export const useFilterSelector = ({ setFilter, filterOptions }: UseTransactionFilter) => {
    const { t } = useTranslation(['transactions', 'date_picker', 'wallet']);
    const { showSelectionSheet } = useAlerts();
    const { showCalendarSheetModal } = useCalendarSheet();
    const hasSocialWallet = useHasSocialWallet();

    const createFilterSelector = useCallback(
        (
            options: Array<TransactionFilterOption<TransactionTypesType | TransactionDateOptionsType>>,
            category: TransactionFilterCategory,
            defaultOption: TransactionFilterOption<TransactionTypesType | TransactionDateOptionsType> &
                CalendarSheetProps
        ) => {
            const selection = getSelection(
                options,
                defaultOption,
                filterOptions[category] as UseTransactionFilter['filterOptions']
            );
            const { onApply, selectDate } = defaultOption;
            showSelectionSheet({
                items: options.map(option => ({
                    id: option.id,
                    label: t(getSocialWalletFilterLabelKey(option.label, hasSocialWallet)),
                })),
                title: category === 'type' ? t('transaction_type') : t('date_picker:choose_a_date_or_date_range'),
                selection: {
                    id: selection.id,
                    label: t(selection.label, { defaultValue: selection.label }),
                },
                onItemSelected: filterType => {
                    if (filterType.id === TransactionDateOptions.DATE_RANGE) {
                        setTimeout(() => {
                            showCalendarSheetModal({ onApply, selectDate });
                        }, 200);
                    } else {
                        setFilter(category, filterType.id);
                    }
                },
            });
        },
        [filterOptions, showSelectionSheet, t, showCalendarSheetModal, setFilter, hasSocialWallet]
    );

    return createFilterSelector;
};
